import { encryptionHelper } from "helpers/encryption"
import _, { isEmpty, isUndefined } from "lodash"
import { Stores, updateData } from "services/indexedDB"
import forge from "node-forge"
import Axios from "axios"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getTabId } from "utils/tabId"
import {
  getUserEmail,
  getUserMasterKey,
  getUserVaultKey,
  setUserVaultKey,
} from "helpers/local_storage"
import { runtimeKeyStore } from "stores/runtimeKeyStore"
import { umamiTracking } from "helpers/umami.client"
import { EVENT } from "constants/events"

export enum EncryptionKeys {
  userMasterKey = "userMasterKey",
  userVaultKey = "userVaultKey",
  projectEncryptionKeys = "projectEncryptionKeys",
  currentProjectEncryptionKey = "currentProjectEncryptionKey",
  conversationEncryptionKeys = "conversationEncryptionKeys",
  currentConversationEncryptionKey = "currentConversationEncryptionKey",
  conversationNoteEncryptionKey = "conversationNoteEncryptionKey",
  buildAdditionalInfoEncryptionKeys = "buildAdditionalInfoEncryptionKeys",
}

export const getTabCurrentProjectEncryptionKey = (): string => {
  const key = runtimeKeyStore.getCurrentProjectKey(getTabId())
  return key || ""
}

export const setTabCurrentProjectEncryptionKey = (value: string): void =>
  runtimeKeyStore.setCurrentProjectKey(getTabId(), value)

export const getTabCurrentConversationEncryptionKey = (): string => {
  const key = runtimeKeyStore.getCurrentConversationKey(getTabId())
  return key || ""
}

export const setTabCurrentConversationEncryptionKey = (value: string): void =>
  runtimeKeyStore.setCurrentConversationKey(getTabId(), value)

export interface ProjectSyncKeys {
  projectId: string
  projectKey: string
  conversations: {
    conversationId: string
    conversationKey: string
  }[]
  buildAdditionalInfos: {
    buildAdditionalInfoId: string
    buildAdditionalInfoKey: string
  }[]
}
export type UpdateProjectSensitiveDataRequest = {
  context_data: ProjectSensitiveData
  keys: ProjectSyncKeys
}

export type ProjectSensitiveData = {
  versions: {
    id: string
    code: string
    commit: string
  }[]
  boms: {
    id: string
    additional_json: string
    mouser_data: string
  }[]
  firmwares: {
    id: string
    description: string
  }[]
  softwares: {
    id: string
    description: string
  }[]
  mechanicals: {
    id: string
    description: string
  }[]
  miscellaneous: {
    id: string
    description: string
  }[]
  build_additional_infos: {
    id: string
    description: string
    project_build_id: string
  }[]
  build_extra_infos: {
    id: string
    description: string
  }[]
  logs: {
    id: string
    content: string
  }[]
  project_comments: {
    id: string
    content: string
  }[]
  conversation_messages: {
    id: string
    conversation_id: string
    content: string
  }[]
  conversation_ids: string[]
  build_ids: string[]
}

export const encryptionController = () => {
  let encryptionKey = {
    [EncryptionKeys.userMasterKey]: getUserMasterKey(),
    [EncryptionKeys.userVaultKey]: getUserVaultKey(),
    [EncryptionKeys.projectEncryptionKeys]: "",
    [EncryptionKeys.currentProjectEncryptionKey]: "",
    [EncryptionKeys.conversationEncryptionKeys]: "",
    [EncryptionKeys.currentConversationEncryptionKey]: "",
    [EncryptionKeys.conversationNoteEncryptionKey]: "",
    [EncryptionKeys.buildAdditionalInfoEncryptionKeys]: "",
  }
  const userEmail = getUserEmail()

  // const storeVaultKey = async (encryptedVaultKey: string) => {
  //   const vaultKey = await encryptionGCMHelper.decryptContext(
  //     encryptionKey.userMasterKey,
  //     encryptedVaultKey
  //   )
  //   storeEncryptionKey(EncryptionKeys.userVaultKey, vaultKey as string)
  // }

  const generateMasterKeyAndPasswordHashed = async (
    password: string,
    email: string
  ) => {
    const masterKey = await encryptionHelper.getPBKDFHash(
      password,
      email,
      100000
    )
    const passwordHash = await encryptionHelper.getPBKDFHash(
      masterKey,
      password
    )
    return {
      masterKey,
      passwordHash,
    }
  }

  const generatePasswordHashed = async (password: string) => {
    return encryptionHelper.getPBKDFHash(encryptionKey.userMasterKey, password)
  }

  const generateVaultKeyAndBackUpKeyAndEncryptedVersion = async (
    masterKey: string
  ) => {
    const generatedBackupKey = await Axios.get("/api/auth/backup-key")

    const { backupKey, encryptedBackupKey } = generatedBackupKey.data.data

    const vaultKey = encryptionHelper.createRandomKey()
    const encryptedVaultkey = (await encryptionGCMHelper.encryptContext(
      masterKey,
      vaultKey
    )) as string

    const encryptedBackUpkey = (await encryptionGCMHelper.encryptContext(
      backupKey,
      vaultKey,
      "base64"
    )) as string

    return {
      vaultKey,
      backupKey,
      encryptedBackUpkey,
      encryptedVaultkey,
      encryptedKMSBackupKey: encryptedBackupKey,
    }
  }

  const decryptVaultKeyAndStore = async (
    encryptedVaultKey: string,
    masterKey: string
  ) => {
    if (!encryptedVaultKey) {
      return
    }
    try {
      const vaultKey = (await encryptionGCMHelper.decryptContext(
        masterKey,
        encryptedVaultKey
      )) as string
      if (vaultKey !== encryptedVaultKey && isValidKeyEncryption(vaultKey)) {
        setUserVaultKey(vaultKey)
        localStorage.removeItem(EncryptionKeys.userMasterKey)
        umamiTracking(EVENT.KEY_DECRYPT.VAULT_SUCCESS, {})
      } else {
        umamiTracking(EVENT.KEY_DECRYPT.VAULT_FAILURE, {
          error: "invalid_or_unchanged_vault",
        })
      }
    } catch (e: any) {
      umamiTracking(EVENT.KEY_DECRYPT.VAULT_FAILURE, {
        error: e?.message ?? "unknown",
      })
    }
  }

  const decryptPrivateKeyAndStore = async (
    encryptedPrivateKey: string,
    userId: string,
    masterKey: string,
    buildKeys: {
      id: string
      encryptedKey: string
      additionalId: string
    }[],
    conversationKeys: {
      id: string
      encryptedKey: string
      additionalId: string
    }[]
  ) => {
    if (!encryptedPrivateKey || !userId) {
      return
    }
    try {
      const userVaultKey = getUserVaultKey()

      let privateKey = (await encryptionGCMHelper.decryptContext(
        userVaultKey,
        encryptedPrivateKey
      )) as string
      if (privateKey === encryptedPrivateKey) {
        privateKey = (await encryptionGCMHelper.decryptContext(
          masterKey,
          encryptedPrivateKey
        )) as string
      }
      if (privateKey === encryptedPrivateKey) {
        umamiTracking(EVENT.KEY_DECRYPT.PRIVATE_FAILURE, {
          error: "unchanged_private_key",
        })
        return
      }
      umamiTracking(EVENT.KEY_DECRYPT.PRIVATE_SUCCESS, {})

      const privatePemKey = forge.pki.privateKeyFromPem(privateKey)
      const projectKeys = runtimeKeyStore.getProjectKeys()
      if (!isUndefined(buildKeys) && buildKeys.length) {
        const buildKeyLocal: Record<string, string> = {}
        for (const buildKey of buildKeys) {
          if (buildKey.additionalId) {
            const projectKey = projectKeys[buildKey.additionalId]
            if (projectKey) {
              try {
                buildKeyLocal[buildKey.id] =
                  (await encryptionGCMHelper.decryptContext(
                    projectKey,
                    buildKey.encryptedKey
                  )) as string
                umamiTracking(EVENT.KEY_DECRYPT.BUILD_SUCCESS, {})
              } catch (e: any) {
                umamiTracking(EVENT.KEY_DECRYPT.BUILD_FAILURE, {
                  error: e?.message ?? "gcm_build_key",
                })
              }
            }
          } else {
            try {
              const decryptedKey = privatePemKey.decrypt(
                buildKey.encryptedKey,
                "RSA-OAEP"
              )
              buildKeyLocal[buildKey.id] = decryptedKey
              umamiTracking(EVENT.KEY_DECRYPT.BUILD_SUCCESS, {})
            } catch (error: any) {
              buildKeyLocal[buildKey.id] = buildKey.encryptedKey
              umamiTracking(EVENT.KEY_DECRYPT.BUILD_FAILURE, {
                error: error?.message ?? "rsa_build_key",
              })
            }
          }
        }

        runtimeKeyStore.upsertBuildKeys(buildKeyLocal)
      }

      if (!isUndefined(conversationKeys) && conversationKeys.length) {
        const conversationKeyLocal: Record<string, string> = {}
        for (const conversationKey of conversationKeys) {
          if (conversationKey.additionalId) {
            const projectKey = projectKeys[conversationKey.additionalId]
            if (projectKey) {
              try {
                conversationKeyLocal[conversationKey.id] =
                  (await encryptionGCMHelper.decryptContext(
                    projectKey,
                    conversationKey.encryptedKey
                  )) as string
                umamiTracking(EVENT.KEY_DECRYPT.CONVERSATION_SUCCESS, {})
              } catch (e: any) {
                umamiTracking(EVENT.KEY_DECRYPT.CONVERSATION_FAILURE, {
                  error: e?.message ?? "gcm_conversation_key",
                })
              }
            }
          } else {
            try {
              const decryptedKey = privatePemKey.decrypt(
                conversationKey.encryptedKey,
                "RSA-OAEP"
              )
              conversationKeyLocal[conversationKey.id] = decryptedKey
              umamiTracking(EVENT.KEY_DECRYPT.CONVERSATION_SUCCESS, {})
            } catch (error: any) {
              conversationKeyLocal[conversationKey.id] =
                conversationKey.encryptedKey
              umamiTracking(EVENT.KEY_DECRYPT.CONVERSATION_FAILURE, {
                error: error?.message ?? "rsa_conversation_key",
              })
            }
          }
        }
        runtimeKeyStore.upsertConversationKeys(conversationKeyLocal)
      }
      await updateData(Stores.PrivateKeys, {
        privateKey,
        id: userId,
      })
    } catch (e: any) {
      umamiTracking(EVENT.KEY_DECRYPT.PRIVATE_FAILURE, {
        error: e?.message ?? "unknown",
      })
    }
  }

  const encryptLog = async (message: string, encryptionKey?: string) => {
    const currentProjectEncryptionKey =
      encryptionKey || (getTabCurrentProjectEncryptionKey() as string)
    if (_.isEmpty(currentProjectEncryptionKey)) return message
    return (await encryptionGCMHelper.encryptContext(
      currentProjectEncryptionKey,
      message
    )) as string
  }

  const encryptUnknowFile = async (file: any, encryptionKey?: string) => {
    const readFile = await encryptionHelper.readFile(file)
    let fileArrayBuffer = new Uint8Array(readFile as any)
    const temp = encryptionHelper.toWordArray(fileArrayBuffer)
    if (encryptionKey) {
      return (await encryptionGCMHelper.encryptContext(
        encryptionKey,
        temp
      )) as string
    }
    const currentProjectEncryptionKey =
      encryptionKey || (getTabCurrentProjectEncryptionKey() as string)
    if (
      !currentProjectEncryptionKey ||
      _.isEmpty(currentProjectEncryptionKey)
    ) {
      return file
    }
    return (await encryptionGCMHelper.encryptContext(
      currentProjectEncryptionKey,
      temp
    )) as string
  }
  const getEncryptionKey = (options: {
    type:
      | "conversation"
      | "conversation_note"
      | "project"
      | "build_additional_info"
      | "component-shared"
    dataType: "string" | "file"
    encryptionKey?: string
    relationId?: string
  }) => {
    let key = options.encryptionKey
    if (isEmpty(key)) {
      switch (options.type) {
        case "project":
          key = getTabCurrentProjectEncryptionKey() as string
          if (options.relationId) {
            key = runtimeKeyStore.getProjectKeys()[options.relationId]
          }
          break
        case "conversation":
          key = getTabCurrentConversationEncryptionKey() as string
          if (options.relationId) {
            key = runtimeKeyStore.getConversationKeys()[options.relationId]
          }
          break

        case "conversation_note":
          key = getUserVaultKey()
          break
        case "build_additional_info":
          key = ""
          if (options.relationId) {
            key = runtimeKeyStore.getBuildKeys()[options.relationId]
          }
          break
        default:
          key = ""
          break
      }
    }
    return key
  }
  const encrypt = async (
    data: any,
    options: {
      type:
        | "conversation"
        | "conversation_note"
        | "project"
        | "build_additional_info"
        | "component-shared"
      dataType: "string" | "file"
      encryptionKey?: string
      relationId?: string
    }
  ) => {
    //get key
    const key = getEncryptionKey(options)
    if (!key || isEmpty(key)) {
      return data
    }
    if (options.dataType === "string") {
      return (await encryptionGCMHelper.encryptContext(key, data)) as string
    }
    const readFile = await encryptionHelper.readFile(data)
    let fileArrayBuffer = new Uint8Array(readFile as any)
    const temp = encryptionHelper.toWordArray(fileArrayBuffer)
    return encryptionGCMHelper.encryptContext(key, temp)
  }

  const decrypt = async (
    data: any,
    options: {
      type:
        | "conversation"
        | "conversation_note"
        | "project"
        | "build_additional_info"
        | "component-shared"
      dataType: "string" | "file"
      encryptionKey?: string
      relationId?: string
    },
    isLogged: boolean = true
  ) => {
    //get key
    const key = getEncryptionKey(options)
    if (!key || isEmpty(key)) {
      return data
    }
    return encryptionGCMHelper.decryptContext(key, data)
  }

  const setCurrentProjectKey = (projectId: string) => {
    const projectKeys = runtimeKeyStore.getProjectKeys()
    setTabCurrentProjectEncryptionKey(projectKeys[projectId] || "")
  }
  const decryptProjectKey = async (encryptedKey: string) => {
    const userVaultKey = getUserVaultKey()
    return await encryptionGCMHelper.decryptContext(userVaultKey, encryptedKey)
  }

  const isValidKeyEncryption = (key: string) => {
    if (isEmpty(key)) return false
    if (key === "null" || key === "undefined") return false
    // AES-256 hex key: exactly 64 hex characters (32 bytes)
    return /^[0-9a-fA-F]{64}$/.test(key)
  }

  return {
    EncryptionKeys,
    generateMasterKeyAndPasswordHashed,
    generateVaultKeyAndBackUpKeyAndEncryptedVersion,
    decryptVaultKeyAndStore,
    ...encryptionKey,
    userEmail,
    generatePasswordHashed,
    encryptLog,
    encryptUnknowFile,
    setCurrentProjectKey,
    decryptProjectKey,
    decryptPrivateKeyAndStore,
    encrypt,
    decrypt,
    isValidKeyEncryption,
  }
}
